import React, { useState } from "react";

import { Edi837Claim } from "../types";
import { formatDrgFromFields, drgFieldValue } from "../drgDisplay";
import {
  format837DischargeDateTime,
  get837ServiceDates,
} from "../institutionalDates";
import {
  formatDisplayDate,
  formatDateRange,
  formatInstant,
  formatAgeYearsFromBirthDate,
} from "../../../helpers/dataHelper";
import { CollapsibleSection } from "../CollapsibleSection";
import {
  formatCurrency,
  formatAddress,
  formatEntityName,
  formatPhone,
  extractProviderByRole,
  getModifiersFromLine,
  facilityTypeVsPlaceOfServiceVisibility,
} from "../viewerHelpers";
import Field from "./Field";
import TabButton from "./TabButton";
import ExpandCollapseToggle from "./ExpandCollapseToggle";
import TabBarWithArrows from "./TabBarWithArrows";
import ClaimServiceLineRow from "./ClaimServiceLineRow";

const Claim837Viewer: React.FC<{ claim: Edi837Claim }> = ({ claim }) => {
  const [activeTab, setActiveTab] = useState<string>("claim");
  const [allExpanded, setAllExpanded] = useState(true);
  const [serviceLinesExpanded, setServiceLinesExpanded] = useState(false);

  const subscriber = claim.subscriber;
  const subscriberPerson = subscriber?.person;
  const payer = subscriber?.payer;
  const patient = claim.patient;
  const patientPerson = patient?.person;

  // Check if patient is different from subscriber
  // Patient is considered different if they have different identifiers or names
  const isPatientDifferent =
    patientPerson &&
    subscriberPerson &&
    ((patientPerson.identifier &&
      subscriberPerson.identifier &&
      patientPerson.identifier !== subscriberPerson.identifier) ||
      (formatEntityName(patientPerson) !== formatEntityName(subscriberPerson) &&
        formatEntityName(patientPerson) !== "-" &&
        formatEntityName(subscriberPerson) !== "-"));

  // Get the best address to display (prefer subscriber, fallback to patient)
  const displayAddress = subscriberPerson?.address || patientPerson?.address;

  const totalCharges =
    claim.serviceLines?.reduce(
      (sum, line) => sum + (line.chargeAmount || 0),
      0,
    ) ||
    claim.chargeAmount ||
    0;

  // Hide columns when no service lines have values
  const hasAnyModifiers =
    claim.serviceLines?.some((line) => getModifiersFromLine(line).length > 0) ??
    false;
  const hasAnyRevOrNdc =
    claim.serviceLines?.some(
      (line) => !!(line.revenueCode?.code || line.drug?.code),
    ) ?? false;

  // Resolve authorization number from multiple sources
  const authorizationNumber = (() => {
    if (claim.priorAuthorizationNumber) return claim.priorAuthorizationNumber;
    if (claim.conditions) {
      const authCondition = claim.conditions.find(
        (c) => c.code === "G1" || c.code === "G2",
      );
      if (authCondition) return authCondition.desc || authCondition.code;
    }
    return undefined;
  })();

  // Extract providers from providers[] array if separate fields not present
  const attendingProvider =
    claim.attendingProvider ||
    extractProviderByRole(claim.providers, "ATTENDING");
  const renderingProvider =
    claim.renderingProvider ||
    extractProviderByRole(claim.providers, "RENDERING");
  const operatingProvider =
    claim.operatingProvider ||
    extractProviderByRole(claim.providers, "OPERATING");
  // const referringProvider = claim.referringProvider || extractProviderByRole(claim.providers, 'REFERRING');

  const claimDrg = formatDrgFromFields(claim.drg, undefined);

  const { showFacilityType, showPlaceOfService, facilityLabel } =
    facilityTypeVsPlaceOfServiceVisibility(claim.transaction?.transactionType);

  const serviceDates = get837ServiceDates(claim);
  const ageAsOfDate = serviceDates.from || serviceDates.to;

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="bg-white px-4 pb-2 pt-1">
        {/* Tabs */}
        <TabBarWithArrows>
          <TabButton
            active={activeTab === "claim"}
            onClick={() => setActiveTab("claim")}
          >
            Claim Details
          </TabButton>
          <TabButton
            active={activeTab === "serviceLines"}
            onClick={() => setActiveTab("serviceLines")}
            count={claim.serviceLines?.length}
          >
            Service Lines
          </TabButton>
          <TabButton
            active={activeTab === "diagnoses"}
            onClick={() => setActiveTab("diagnoses")}
            count={(claim.diags?.length || 0) + (claim.procs?.length || 0)}
          >
            Dx/Procedures
          </TabButton>
          <TabButton
            active={activeTab === "transaction"}
            onClick={() => setActiveTab("transaction")}
          >
            Transaction
          </TabButton>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <ExpandCollapseToggle
              expanded={allExpanded}
              onToggle={(next) => {
                setAllExpanded(next);
                setServiceLinesExpanded(next);
              }}
            />
          </div>
        </TabBarWithArrows>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        {activeTab === "claim" && (
          <div>
            <CollapsibleSection
              title="Key Information"
              defaultOpen={allExpanded}
            >
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <Field
                  label="Patient Control #"
                  value={claim.patientControlNumber}
                />
                <Field
                  label="Total Charge"
                  value={formatCurrency(totalCharges)}
                />
                {showFacilityType && (
                  <Field
                    label={facilityLabel}
                    value={
                      claim.facilityCode
                        ? `${claim.facilityCode.code} - ${claim.facilityCode.desc || ""}`
                        : "-"
                    }
                  />
                )}
                {showPlaceOfService && (
                  <Field
                    label="Place of Service"
                    value={claim.placeOfServiceType || "-"}
                  />
                )}
                <Field
                  label="Frequency"
                  value={
                    claim.frequencyCode
                      ? `${claim.frequencyCode.code} - ${claim.frequencyCode.desc || ""}`
                      : "-"
                  }
                />
                <Field
                  label="Service Dates"
                  value={formatDateRange(serviceDates.from, serviceDates.to)}
                />
                {authorizationNumber && (
                  <Field label="Authorization #" value={authorizationNumber} />
                )}
                {claim.medicalRecordNumber && (
                  <Field
                    label="Medical Record #"
                    value={claim.medicalRecordNumber}
                  />
                )}
                {claim.accidentState && (
                  <Field label="Accident State" value={claim.accidentState} />
                )}
                {claimDrg && (
                  <Field label="DRG" value={drgFieldValue(claimDrg)} />
                )}
                {claim.drgWeight != null && (
                  <Field label="DRG Weight" value={String(claim.drgWeight)} />
                )}
              </div>
              {(claim.claimNote || claim.billingNote) && (
                <div className="mt-3 space-y-2">
                  {claim.claimNote && (
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Claim Note:</span>{" "}
                      {claim.claimNote}
                    </div>
                  )}
                  {claim.billingNote && (
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Billing Note:</span>{" "}
                      {claim.billingNote}
                    </div>
                  )}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title="Subscriber / Patient"
              defaultOpen={allExpanded}
              headerRight={
                <span className="text-sm text-gray-500">
                  {formatEntityName(subscriberPerson)}
                </span>
              }
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Name"
                  value={formatEntityName(subscriberPerson)}
                />
                <Field label="Member ID" value={subscriberPerson?.identifier} />
                <Field
                  label="Birth Date"
                  value={formatDisplayDate(subscriberPerson?.birthDate)}
                />
                <Field
                  label="Age"
                  value={formatAgeYearsFromBirthDate(
                    subscriberPerson?.birthDate,
                    ageAsOfDate,
                  )}
                />
                <Field label="Gender" value={subscriberPerson?.gender} />
                <Field label="Address" value={formatAddress(displayAddress)} />
                <Field
                  label="Relationship"
                  value={subscriber?.relationshipType}
                />
                <Field
                  label="Group/Policy #"
                  value={subscriber?.groupOrPolicyNumber}
                />
                <Field
                  label="Payer Responsibility"
                  value={subscriber?.payerResponsibilitySequence}
                />
              </div>
            </CollapsibleSection>

            {/* Separate Patient section if patient is different from subscriber */}
            {isPatientDifferent && patientPerson && (
              <CollapsibleSection
                title="Patient"
                defaultOpen={allExpanded}
                headerRight={
                  <span className="text-xs text-gray-500">
                    {formatEntityName(patientPerson)}
                  </span>
                }
              >
                <div className="grid grid-cols-3 gap-4">
                  <Field label="Name" value={formatEntityName(patientPerson)} />
                  <Field label="Member ID" value={patientPerson?.identifier} />
                  <Field
                    label="Birth Date"
                    value={formatDisplayDate(patientPerson?.birthDate)}
                  />
                  <Field
                    label="Age"
                    value={formatAgeYearsFromBirthDate(
                      patientPerson?.birthDate,
                      ageAsOfDate,
                    )}
                  />
                  <Field label="Gender" value={patientPerson?.gender} />
                  <Field
                    label="Address"
                    value={formatAddress(patientPerson?.address)}
                  />
                </div>
              </CollapsibleSection>
            )}

            {claim.otherSubscribers && claim.otherSubscribers.length > 0 && (
              <CollapsibleSection
                title={
                  claim.otherSubscribers.length > 1
                    ? `Other Subscribers (${claim.otherSubscribers.length})`
                    : "Other Subscriber"
                }
                defaultOpen={allExpanded}
                headerRight={
                  <span className="text-sm text-gray-500">
                    {claim.otherSubscribers
                      .map(
                        (os) =>
                          os.payer?.lastNameOrOrgName ||
                          formatEntityName(os.person),
                      )
                      .filter((s) => s && s !== "-")
                      .join(", ")}
                  </span>
                }
              >
                <div className="space-y-4">
                  {claim.otherSubscribers.map((otherSub, idx) => {
                    const osPerson = otherSub.person;
                    const osPayer = otherSub.payer;
                    return (
                      <div
                        key={idx}
                        className={
                          idx > 0 ? "pt-4 border-t border-gray-200" : ""
                        }
                      >
                        {claim.otherSubscribers!.length > 1 && (
                          <div className="text-sm font-semibold text-gray-700 mb-2">
                            {`#${idx + 1}`}
                            {otherSub.payerResponsibilitySequence &&
                              ` — ${otherSub.payerResponsibilitySequence}`}
                            {osPayer?.lastNameOrOrgName &&
                              ` — ${osPayer.lastNameOrOrgName}`}
                          </div>
                        )}
                        <div
                          className="grid gap-4"
                          style={{
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(150px, 1fr))",
                          }}
                        >
                          <Field
                            label="Name"
                            value={formatEntityName(osPerson)}
                          />
                          <Field
                            label="Member ID"
                            value={osPerson?.identifier}
                          />
                          <Field
                            label="Birth Date"
                            value={formatDisplayDate(osPerson?.birthDate)}
                          />
                          <Field label="Gender" value={osPerson?.gender} />
                          <Field
                            label="Address"
                            value={formatAddress(osPerson?.address)}
                          />
                          <Field
                            label="Relationship"
                            value={otherSub.relationshipType}
                          />
                          <Field
                            label="Group/Policy #"
                            value={otherSub.groupOrPolicyNumber}
                          />
                          <Field
                            label="Group Name"
                            value={otherSub.groupName}
                          />
                          <Field
                            label="Payer Responsibility"
                            value={otherSub.payerResponsibilitySequence}
                          />
                          <Field
                            label="Claim Filing Indicator"
                            value={otherSub.claimFilingIndicatorCode}
                          />
                          <Field
                            label="Insurance Plan Type"
                            value={otherSub.insurancePlanType}
                          />
                          <Field
                            label="Payer"
                            value={osPayer?.lastNameOrOrgName}
                          />
                          <Field label="Payer ID" value={osPayer?.identifier} />
                          {otherSub.payerPaidAmount != null && (
                            <Field
                              label="Payer Paid Amt"
                              value={formatCurrency(otherSub.payerPaidAmount)}
                            />
                          )}
                          {otherSub.nonCoveredAmount != null && (
                            <Field
                              label="Non-Covered Amt"
                              value={formatCurrency(otherSub.nonCoveredAmount)}
                            />
                          )}
                          {otherSub.remainingPatientLiabilityAmount != null && (
                            <Field
                              label="Remaining Pt Liability"
                              value={formatCurrency(
                                otherSub.remainingPatientLiabilityAmount,
                              )}
                            />
                          )}
                          <Field
                            label="Prior Auth #"
                            value={otherSub.payerPriorAuthorizationNumber}
                          />
                          <Field
                            label="Referral #"
                            value={otherSub.payerReferralNumber}
                          />
                          <Field
                            label="Payer Claim Control #"
                            value={otherSub.payerClaimControlNumber}
                          />
                        </div>
                        {otherSub.adjustments &&
                          otherSub.adjustments.length > 0 && (
                            <div className="mt-3">
                              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                                Adjustments (CAS)
                              </div>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-[11px] text-gray-500 uppercase">
                                    <th className="text-left px-2 py-0.5">
                                      Group
                                    </th>
                                    <th className="text-left px-2 py-0.5">
                                      Code
                                    </th>
                                    <th className="text-left px-2 py-0.5">
                                      Reason
                                    </th>
                                    <th className="text-right px-2 py-0.5">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {otherSub.adjustments.map((adj, i) => (
                                    <tr
                                      key={i}
                                      className="border-t border-gray-100"
                                    >
                                      <td className="px-2 py-0.5 text-gray-600">
                                        {adj.group || "-"}
                                      </td>
                                      <td className="px-2 py-0.5 font-mono text-gray-900">
                                        {adj.reason?.code ||
                                          adj.reasonCode ||
                                          "-"}
                                      </td>
                                      <td className="px-2 py-0.5 text-gray-600">
                                        {adj.reason?.desc || "-"}
                                      </td>
                                      <td className="px-2 py-0.5 text-right text-red-600">
                                        {formatCurrency(adj.amount)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>
            )}

            <CollapsibleSection
              title="Payer"
              defaultOpen={allExpanded}
              headerRight={
                <span className="text-sm text-gray-500">
                  {payer?.lastNameOrOrgName}
                </span>
              }
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Name" value={payer?.lastNameOrOrgName} />
                <Field label="Payer ID" value={payer?.identifier} />
                <Field label="ID Type" value={payer?.identificationType} />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Billing Provider"
              defaultOpen={allExpanded}
              headerRight={
                <span className="text-sm text-gray-500">
                  NPI: {claim.billingProvider?.identifier}
                </span>
              }
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Name"
                  value={claim.billingProvider?.lastNameOrOrgName}
                />
                <Field label="NPI" value={claim.billingProvider?.identifier} />
                <Field label="Tax ID" value={claim.billingProvider?.taxId} />
                <Field
                  label="Address"
                  value={formatAddress(claim.billingProvider?.address)}
                />
                <Field
                  label="Contact"
                  value={claim.billingProvider?.contacts?.[0]?.name}
                />
                <Field
                  label="Phone"
                  value={formatPhone(
                    claim.billingProvider?.contacts?.[0]?.contactNumbers?.[0]
                      ?.number,
                  )}
                />
              </div>
            </CollapsibleSection>

            {/* Other Physicians (Attending, Operating, Other) */}
            {(attendingProvider ||
              operatingProvider ||
              claim.otherProvider) && (
              <CollapsibleSection
                title="Other Physicians"
                defaultOpen={allExpanded}
              >
                <div className="space-y-4">
                  {attendingProvider && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">
                        Attending Physician
                      </div>
                      <div
                        className="grid gap-4"
                        style={{
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(150px, 1fr))",
                        }}
                      >
                        <Field
                          label="Name"
                          value={formatEntityName(attendingProvider)}
                        />
                        <Field
                          label="NPI"
                          value={attendingProvider.identifier}
                        />
                        <Field
                          label="Specialty"
                          value={
                            attendingProvider.qualification ||
                            attendingProvider.providerTaxonomy?.desc
                          }
                        />
                      </div>
                    </div>
                  )}
                  {operatingProvider && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">
                        Operating Physician
                      </div>
                      <div
                        className="grid gap-4"
                        style={{
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(150px, 1fr))",
                        }}
                      >
                        <Field
                          label="Name"
                          value={formatEntityName(operatingProvider)}
                        />
                        <Field
                          label="NPI"
                          value={operatingProvider.identifier}
                        />
                        <Field
                          label="Specialty"
                          value={
                            operatingProvider.qualification ||
                            operatingProvider.providerTaxonomy?.desc
                          }
                        />
                      </div>
                    </div>
                  )}
                  {claim.otherProvider && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">
                        Other Physician
                      </div>
                      <div
                        className="grid gap-4"
                        style={{
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(150px, 1fr))",
                        }}
                      >
                        <Field
                          label="Name"
                          value={formatEntityName(claim.otherProvider)}
                        />
                        <Field
                          label="NPI"
                          value={claim.otherProvider.identifier}
                        />
                        <Field
                          label="Specialty"
                          value={
                            claim.otherProvider.qualification ||
                            claim.otherProvider.providerTaxonomy?.desc
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleSection>
            )}

            {/* Rendering Provider */}
            {renderingProvider && (
              <CollapsibleSection
                title="Rendering Provider"
                defaultOpen={allExpanded}
                headerRight={
                  <span className="text-xs text-gray-500">
                    NPI: {renderingProvider?.identifier}
                  </span>
                }
              >
                <div className="grid grid-cols-3 gap-4">
                  <Field
                    label="Name"
                    value={formatEntityName(renderingProvider)}
                  />
                  <Field label="NPI" value={renderingProvider?.identifier} />
                  <Field label="Tax ID" value={renderingProvider?.taxId} />
                  <Field
                    label="Address"
                    value={formatAddress(renderingProvider?.address)}
                  />
                  <Field
                    label="Contact"
                    value={renderingProvider?.contacts?.[0]?.name}
                  />
                  <Field
                    label="Phone"
                    value={formatPhone(
                      renderingProvider?.contacts?.[0]?.contactNumbers?.[0]
                        ?.number,
                    )}
                  />
                </div>
              </CollapsibleSection>
            )}

            {/* Admission Information (Institutional/UB04) */}
            {(claim.admissionTypeCode ||
              claim.admissionSourceCode ||
              claim.patientStatusCode ||
              claim.admissionDateAndHour ||
              claim.dischargeTime) && (
              <CollapsibleSection
                title="Admission Information"
                defaultOpen={allExpanded}
              >
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Admission Type Code"
                    value={claim.admissionTypeCode}
                  />
                  <Field
                    label="Admission Source Code"
                    value={claim.admissionSourceCode}
                  />
                  <Field
                    label="Patient Status Code"
                    value={claim.patientStatusCode}
                  />
                  <Field
                    label="Admission Date/Hour"
                    value={
                      claim.admissionDateAndHour
                        ? formatInstant(claim.admissionDateAndHour)
                        : undefined
                    }
                  />
                  <Field
                    label="Discharge Date/Hour"
                    value={format837DischargeDateTime(claim)}
                  />
                </div>
              </CollapsibleSection>
            )}

            {/* Clinical Trial / Demonstration Project */}
            {claim.demonstrationProjectIdentifier && (
              <CollapsibleSection
                title="Clinical Trial Information"
                defaultOpen={allExpanded}
              >
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Demonstration Project ID"
                    value={claim.demonstrationProjectIdentifier}
                  />
                </div>
              </CollapsibleSection>
            )}

            {/* Authorization Number */}
            {authorizationNumber && (
              <CollapsibleSection
                title="Authorization"
                defaultOpen={allExpanded}
              >
                <div className="grid grid-cols-3 gap-4">
                  <Field
                    label="Authorization Number"
                    value={authorizationNumber}
                  />
                </div>
              </CollapsibleSection>
            )}

            {/* Occurrence Codes (UB04 FL 31-34) */}
            {claim.occurrences && claim.occurrences.length > 0 && (
              <CollapsibleSection
                title="Occurrence Codes"
                defaultOpen={allExpanded}
              >
                <div className="space-y-2">
                  {claim.occurrences.map((occ, idx) => (
                    <div
                      key={idx}
                      className="p-2 bg-gray-50 rounded flex items-baseline gap-3"
                    >
                      <span className="font-mono text-md text-gray-900 shrink-0">
                        {occ.code}
                      </span>
                      <span className="text-md text-gray-600">{occ.desc}</span>
                      {occ.occurrenceDate && (
                        <span className="text-sm text-gray-500 ml-auto shrink-0">
                          {formatDisplayDate(occ.occurrenceDate)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Occurrence Span Codes (UB04 FL 35-36) */}
            {claim.occurrenceSpans && claim.occurrenceSpans.length > 0 && (
              <CollapsibleSection
                title="Occurrence Span Codes"
                defaultOpen={allExpanded}
              >
                <div className="space-y-2">
                  {claim.occurrenceSpans.map((span, idx) => (
                    <div
                      key={idx}
                      className="p-2 bg-gray-50 rounded flex items-baseline gap-3"
                    >
                      <span className="font-mono text-md text-gray-900 shrink-0">
                        {span.code}
                      </span>
                      <span className="text-md text-gray-600">{span.desc}</span>
                      <span className="text-sm text-gray-500 ml-auto shrink-0">
                        {span.occurrenceDate
                          ? formatDisplayDate(span.occurrenceDate)
                          : "-"}
                        {span.occurrenceEndDate
                          ? ` — ${formatDisplayDate(span.occurrenceEndDate)}`
                          : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Condition Codes (UB04 FL 18-28) */}
            {claim.conditions && claim.conditions.length > 0 && (
              <CollapsibleSection
                title="Condition Codes"
                defaultOpen={allExpanded}
              >
                <div className="space-y-2">
                  {claim.conditions.map((cond, idx) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded">
                      <div className="font-mono text-md text-gray-900">
                        {cond.code}
                      </div>
                      <div className="text-md text-gray-600">{cond.desc}</div>
                      {cond.occurrenceDate && (
                        <div className="text-sm text-gray-500 mt-1">
                          Date: {formatDisplayDate(cond.occurrenceDate)}
                        </div>
                      )}
                      {cond.instructionDesc && (
                        <div className="text-sm text-gray-500 mt-1">
                          {cond.instructionDesc}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Value Codes (UB04 FL 39-41) */}
            {claim.valueInfos && claim.valueInfos.length > 0 && (
              <CollapsibleSection title="Value Codes" defaultOpen={allExpanded}>
                <div className="space-y-2">
                  {claim.valueInfos.map((val, idx) => (
                    <div
                      key={idx}
                      className="p-2 bg-gray-50 rounded flex items-baseline gap-3"
                    >
                      <span className="font-mono text-md text-gray-900 shrink-0">
                        {val.code}
                      </span>
                      <span className="text-md text-gray-600">{val.desc}</span>
                      {val.amount != null && (
                        <span className="text-sm font-medium text-gray-700 ml-auto shrink-0">
                          {formatCurrency(val.amount)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}
          </div>
        )}

        {activeTab === "serviceLines" && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left w-8"></th>
                    <th className="px-3 py-2 text-left">Line</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    {hasAnyModifiers && (
                      <th className="px-3 py-2 text-left">Modifiers</th>
                    )}
                    <th className="px-3 py-2 text-right">Units</th>
                    <th className="px-3 py-2 text-right">Charge</th>
                    {hasAnyRevOrNdc && (
                      <th className="px-3 py-2 text-left">Rev/NDC</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {claim.serviceLines?.map((line, idx) => (
                    <ClaimServiceLineRow
                      key={idx}
                      line={line}
                      index={idx}
                      allExpanded={serviceLinesExpanded}
                      showModifiersColumn={hasAnyModifiers}
                      showRevNdcColumn={hasAnyRevOrNdc}
                    />
                  )) || (
                    <tr>
                      <td
                        colSpan={
                          5 +
                          (hasAnyModifiers ? 1 : 0) +
                          (hasAnyRevOrNdc ? 1 : 0)
                        }
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No service lines
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr className="font-medium">
                    <td
                      colSpan={4 + (hasAnyModifiers ? 1 : 0)}
                      className="px-3 py-2 text-right text-md"
                    >
                      Total:
                    </td>
                    <td className="px-3 py-2 text-right text-md">
                      {formatCurrency(totalCharges)}
                    </td>
                    {hasAnyRevOrNdc && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {activeTab === "diagnoses" && (
          <div className="space-y-4">
            {/* Diagnoses Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">
                  Diagnosis Codes (ICD-10-CM)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-sm text-gray-500 uppercase">
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Code</th>
                      <th className="px-3 py-2 text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claim.diags?.map((diag, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-md font-medium bg-orange-100 text-orange-800">
                            {diag.subType}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-md">
                          {diag.formattedCode || diag.code}
                        </td>
                        <td className="px-3 py-2 text-md text-gray-600">
                          {diag.desc}
                        </td>
                      </tr>
                    )) || (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-4 text-center text-gray-500"
                        >
                          No diagnoses
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Procedures Table (ICD-10-PCS) */}
            {claim.procs && claim.procs.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Procedure Codes (ICD-10-PCS)
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-sm text-gray-500 uppercase">
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Code</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claim.procs.map((proc, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 rounded text-md font-medium bg-purple-100 text-purple-800">
                              {proc.subType}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-md">
                            {proc.code}
                          </td>
                          <td className="px-3 py-2 text-md text-gray-600">
                            {proc.desc}
                          </td>
                          <td className="px-3 py-2 text-md text-gray-500">
                            {proc.occurrenceDate
                              ? formatDisplayDate(proc.occurrenceDate)
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "transaction" && claim.transaction && (
          <div>
            <CollapsibleSection
              title="Transaction Details"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Transaction Type"
                  value={claim.transaction.transactionType}
                />
                <Field
                  label="Control Number"
                  value={claim.transaction.controlNumber}
                />
                <Field
                  label="Transaction Set"
                  value={claim.transaction.transactionSetIdentifierCode}
                />
                <Field
                  label="Creation Date"
                  value={formatDisplayDate(claim.transaction.creationDate)}
                />
                <Field
                  label="Purpose Code"
                  value={claim.transaction.purposeCode}
                />
                <Field
                  label="Implementation Ref"
                  value={claim.transaction.implementationConventionReference}
                />
              </div>
            </CollapsibleSection>
            {claim.transaction.sender && (
              <CollapsibleSection
                title="Sender (Submitter)"
                defaultOpen={allExpanded}
              >
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Name"
                    value={claim.transaction.sender.lastNameOrOrgName}
                  />
                  <Field
                    label="ID"
                    value={claim.transaction.sender.identifier}
                  />
                  <Field
                    label="Contact"
                    value={claim.transaction.sender.contacts?.[0]?.name}
                  />
                  <Field
                    label="Phone"
                    value={formatPhone(
                      claim.transaction.sender.contacts?.[0]
                        ?.contactNumbers?.[0]?.number,
                    )}
                  />
                </div>
              </CollapsibleSection>
            )}
            {claim.transaction.receiver && (
              <CollapsibleSection title="Receiver" defaultOpen={allExpanded}>
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Name"
                    value={claim.transaction.receiver.lastNameOrOrgName}
                  />
                  <Field
                    label="ID"
                    value={claim.transaction.receiver.identifier}
                  />
                </div>
              </CollapsibleSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Claim837Viewer;
